import { useEffect } from 'react';

import { useLocation } from 'react-router';

import { getHelpUrl } from '@change-corgi/config/help';
import { useI18n } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { useStarterDashboardV2BannerState } from 'src/app/pages/starterDashboardV2/pageContext';
import { useLocale } from 'src/app/shared/hooks/l10n';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useCurrentAuthenticatedUser } from 'src/app/shared/hooks/session';

import { getDownloadExportUrl } from './api';
import { EXPORT_DOWNLOAD_ERROR_MAP, exportDownloadErrorCodes } from './constants';

export function useDownloadExport() {
	const utilityContext = useUtilityContext();
	const download = useQueryParamValue('download');
	const { actions } = useStarterDashboardV2BannerState();
	const authenticatedUser = useCurrentAuthenticatedUser();
	const { pathname, search } = useLocation();
	const { translate, typedTranslateHtml } = useI18n();
	const navigate = useNavigate();
	const locale = useLocale();

	useEffect(() => {
		(async () => {
			// if user is not authenticated and has a download param, redirect and return
			if (!authenticatedUser && download) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join?redirectTo=${params}`);
				return;
			}

			// else process the download and "navigate" to the url
			// navigation will only download the file, but keep the user on dashboard
			if (authenticatedUser && download) {
				try {
					const response = await getDownloadExportUrl({ downloadId: download }, utilityContext);
					actions.addBanner({
						message: translate('corgi.petition.starter_dashboard.export_download.starting'),
						severity: 'success',
						dismissable: true,
					});
					navigate(response.url);
				} catch (e) {
					const parsedError = parseError(e);
					actions.addBanner({
						message: typedTranslateHtml(EXPORT_DOWNLOAD_ERROR_MAP[parsedError].message, {
							helpUrl: getHelpUrl(locale, 'contact-us'),
						}),
						severity: 'critical',
						dismissable: true,
					});
				}
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
}
function parseError(error: unknown) {
	const err = error instanceof Error ? error.message : undefined;
	return exportDownloadErrorCodes.find((val) => val === err) ?? EXPORT_DOWNLOAD_ERROR_MAP.GENERIC_ERROR.code;
}
